// Shared tournament UI helpers (time formatting, labels, small atoms).
import { Lock } from "lucide-react";
import { toast } from "sonner";

import { tournaments } from "../../lib/tournamentApi";

export function fmtSlot(iso, tz, withDay = true) {
  if (!iso) return "";
  const opts = { hour: "numeric", minute: "2-digit", timeZone: tz };
  if (withDay) opts.weekday = "short";
  try { return new Intl.DateTimeFormat(undefined, opts).format(new Date(iso)); } catch { return iso; }
}

export function roundLabel(round, maxRound) {
  if (round === maxRound) return "Final";
  if (round === maxRound - 1) return "Semifinal";
  return `Round ${round}`;
}

export function gameLabel(g) {
  return `R${g.round} G${g.slot + 1}`;
}

export function teamName(teamsById, id) {
  return id ? teamsById[id]?.name || "TBD" : "TBD";
}

export const inputStyle = {
  padding: "10px 12px", borderRadius: 10, border: "0.5px solid var(--chalk-border)",
  background: "#FBF8F2", color: "var(--chalk-board)", fontSize: 14, width: "100%",
};

export const pillBtn = (active, tone = "var(--chalk-board)") => ({
  padding: "6px 12px", borderRadius: 999, whiteSpace: "nowrap",
  background: active ? tone : "transparent", color: active ? "#F5F0E8" : "var(--chalk-board)",
  border: "0.5px solid var(--chalk-border)", cursor: "pointer", fontSize: 12, fontWeight: 700,
  transition: "background-color 150ms ease, color 150ms ease",
});

export function HubGate({ data, feature, onChanged }) {
  const canMock = data.capabilities?.preview_upgrade;
  async function upgrade() {
    try {
      await tournaments.previewUpgrade(data.tournament.id);
      toast.success("Upgraded to The Tournament Hub (preview).");
      await onChanged();
    } catch (e) { toast.error(e.message || "Couldn't upgrade."); }
  }
  return (
    <div data-testid="hub-gate" style={{
      background: "#fff", border: "0.5px solid #DAD8D1", borderLeft: "2px solid var(--green-mid)",
      borderRadius: 14, padding: 18, marginBottom: 12,
    }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 15, fontWeight: 600 }}>
        <Lock size={16} aria-hidden="true" /> {feature} is part of The Tournament Hub.
      </div>
      <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 6, lineHeight: 1.5 }}>
        Divisions, schedule by location, check-in, rosters, announcements and fan alerts — up to 64 teams per division.
      </div>
      {data.my_role === "director" && (
        <a data-testid="hub-upgrade-link" href={`/app/tournaments/${data.tournament.id}/upgrade`} style={{
          display: "inline-block", marginTop: 12, marginRight: 8, background: "var(--green-mid)", color: "#fff", textDecoration: "none",
          borderRadius: 999, padding: "8px 16px", fontWeight: 700, fontSize: 13,
        }}>See Hub pricing</a>
      )}
      {canMock && (
        <button data-testid="hub-preview-upgrade" onClick={upgrade} style={{
          marginTop: 12, background: "var(--green-mid)", color: "#fff", border: "none",
          borderRadius: 999, padding: "8px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer",
        }}>
          Instant upgrade (QA mock)
        </button>
      )}
    </div>
  );
}

export function DivisionPicker({ divisions, value, onChange }) {
  if (divisions.length < 2) return null;
  return (
    <div data-testid="division-picker" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 10, paddingBottom: 2 }}>
      {divisions.map((d) => (
        <button key={d.id} data-testid={`division-chip-${d.id}`} onClick={() => onChange(d.id)}
                style={pillBtn(value === d.id, "var(--green-mid)")}>
          {d.name}
        </button>
      ))}
    </div>
  );
}

export function Pill({ tone = "muted", children, testId }) {
  const tones = {
    muted: ["#EEEAE1", "var(--chalk-muted)"], green: ["#E3F1E8", "var(--green-mid)"],
    amber: ["var(--chalk-amber-bg)", "var(--chalk-amber)"], red: ["var(--chalk-red-bg)", "var(--chalk-red)"],
  };
  const [bg, fg] = tones[tone];
  return (
    <span data-testid={testId} style={{ background: bg, color: fg, borderRadius: 999, padding: "2px 8px",
                                        fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}
